import { assert } from "@glideapps/ts-necessities";
import { replaceArrayItem } from "../../Support";
import type { ActionDescription, Formula } from "../../description";

export enum ActionNodeKind {
    Compound = "compound",
    ConditionalFlow = "conditional-flow",
    Primitive = "primitive",
    Flow = "flow",
}

export type ActionNode = PrimitiveActionNode | FlowActionNode | ConditionalFlowNode | CompoundActionNode;

export interface ActionNodeBase {
    readonly key: string;
}

export interface CompoundActionNode extends ActionNodeBase {
    readonly kind: ActionNodeKind.Compound;

    readonly conditionalFlows: readonly ConditionalFlowNode[];
    readonly elseFlow: FlowActionNode | undefined;
}

export interface ConditionalFlowNode extends ActionNodeBase {
    readonly kind: ActionNodeKind.ConditionalFlow;

    readonly condition: Formula;
    readonly flow: FlowActionNode;
}

export interface FlowActionNode extends ActionNodeBase {
    readonly kind: ActionNodeKind.Flow;

    readonly actions: readonly PrimitiveActionNode[];
}

export interface PrimitiveActionNode extends ActionNodeBase {
    readonly kind: ActionNodeKind.Primitive;

    readonly actionDescription: ActionDescription;
}

function validateFlowActionNode(n: FlowActionNode): string | undefined {
    if (n.actions.length === 0) {
        return "Flow must not be empty";
    }
    return undefined;
}

export function validateCompoundActionNode(n: CompoundActionNode): string | undefined {
    if (n.conditionalFlows.length === 0 && n.elseFlow === undefined) {
        return "Must have at least one flow";
    }
    for (const c of n.conditionalFlows) {
        const err = validateFlowActionNode(c.flow);
        if (err !== undefined) return err;
    }

    if (n.elseFlow === undefined) return undefined;

    return validateFlowActionNode(n.elseFlow);
}

export function processCompoundActionFlow(
    compound: CompoundActionNode,
    processFlow: (flow: FlowActionNode) => void
): void {
    for (const flow of compound.conditionalFlows) {
        processFlow(flow.flow);
    }
    if (compound.elseFlow !== undefined) {
        processFlow(compound.elseFlow);
    }
}

export function walkActionNode(node: ActionNode, callback: (node: ActionNode) => ActionNode): ActionNode {
    let result = node;
    switch (result.kind) {
        case ActionNodeKind.Compound:
            let newConditions = result.conditionalFlows;
            let i = 0;
            for (const c of result.conditionalFlows) {
                const r = walkActionNode(c, callback);
                assert(r.kind === ActionNodeKind.ConditionalFlow);
                if (r !== c) {
                    newConditions = replaceArrayItem(newConditions, i, r);
                }
                i++;
            }
            if (result.conditionalFlows !== newConditions) {
                result = {
                    ...result,
                    conditionalFlows: newConditions,
                };
            }
            if (result.elseFlow !== undefined) {
                const elseNode = walkActionNode(result.elseFlow, callback);
                assert(elseNode.kind === ActionNodeKind.Flow);
                if (elseNode !== result.elseFlow) {
                    result = {
                        ...result,
                        elseFlow: elseNode,
                    };
                }
            }
            break;
        case ActionNodeKind.ConditionalFlow:
            const newFlow = walkActionNode(result.flow, callback);
            assert(newFlow.kind === ActionNodeKind.Flow);
            if (newFlow !== result.flow) {
                result = {
                    ...result,
                    flow: newFlow,
                };
            }
            break;
        case ActionNodeKind.Flow:
            let newActions = result.actions;
            let j = 0;
            for (const c of result.actions) {
                const r = walkActionNode(c, callback);
                assert(r.kind === ActionNodeKind.Primitive);
                if (r !== c) {
                    newActions = replaceArrayItem(newActions, j, r);
                }
                j++;
            }
            if (result.actions !== newActions) {
                result = {
                    ...result,
                    actions: newActions,
                };
            }
            break;
    }

    return callback(result);
}
